import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { cn } from "@arcadeai/design-system/lib/utils";
import { Loader2 } from "lucide-react";

type GatewaySubmitButtonProps = {
  isPending: boolean;
  readyToCreate: boolean;
  isEditing: boolean;
  missingRequirements: string[];
};

function SubmitTooltipContent({
  readyToCreate,
  missingRequirements,
  isEditing,
}: {
  readyToCreate: boolean;
  missingRequirements: string[];
  isEditing: boolean;
}) {
  if (readyToCreate) {
    return (
      <p>
        {isEditing
          ? "Ready to save your changes."
          : "Ready to create your MCP gateway."}
      </p>
    );
  }

  return (
    <div className="space-y-1.5">
      <p className="font-medium">
        {isEditing ? "Before you can save it:" : "Before you can create it:"}
      </p>
      <ul className="space-y-1">
        {missingRequirements.map((requirement) => (
          <li className="flex items-center gap-1.5" key={requirement}>
            <span
              aria-hidden
              className="size-1 shrink-0 rounded-full bg-current"
            />
            {requirement}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function GatewaySubmitButton({
  isPending,
  readyToCreate,
  isEditing,
  missingRequirements,
}: GatewaySubmitButtonProps) {
  return (
    <TooltipProvider delay={200}>
      <Tooltip>
        <TooltipTrigger
          render={
            // Not-ready stays activatable so a click/Enter surfaces the toast
            // naming what's missing — so `aria-disabled` reflects only the truly
            // blocked (in-flight) state, not the not-ready one, which would make
            // a screen reader announce "disabled" on a control that still works.
            // The tooltip (wired via the trigger's aria-describedby) explains
            // what's missing. Only an in-flight submit hard-disables to prevent a
            // double POST.
            <Button
              aria-disabled={isPending}
              className={cn(
                "w-full sm:w-auto",
                (isPending || !readyToCreate) && "cursor-not-allowed opacity-50"
              )}
              disabled={isPending}
              size="lg"
              type="submit"
            />
          }
        >
          {isPending ? (
            <Loader2 aria-hidden className="mr-2 size-4 animate-spin" />
          ) : null}
          {isEditing ? "Save changes" : "Create MCP gateway"}
        </TooltipTrigger>
        <TooltipContent>
          <SubmitTooltipContent
            isEditing={isEditing}
            missingRequirements={missingRequirements}
            readyToCreate={readyToCreate}
          />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
